<template>
  <li
    @mouseenter="hoverItem"
    @mouseout="mouseoutItem"
    @mousemove="enableMouseEnter"
    @click.stop="selectOptionClick"
    class="yxt-select-dropdown__item"
    v-show="visible"
    :class="[itemSelectedClass, {
      selected: itemSelected,
      hover: isHover,
      'is-disabled': disabled || groupDisabled || limitReached,
      'is-labelTooltip': labelTooltip,
    }]"
  >
    <yxt-checkbox
      @click.native.prevent
      v-if="select.multipleStyle==='checkbox'"
      v-model="itemSelected"
      :disabled = itemDisabled
      :class="['selected-multiple-checkbox']"></yxt-checkbox>
    <slot>
      <span>{{ currentLabel }}</span>
    </slot>
    <yxt-tooltip :effect="tooltipEffect" :content="labelTooltip" placement="top-start" :offset="10" v-if="labelTooltip" :max-width="labelTooltipWidth">
      <yxt-svg remote-url='' class='color-gray-6 hover-primary-6 ml4 question-svg' width='16px' height='16px' icon-class="question-cirlce-o" />
    </yxt-tooltip>
  </li>
</template>

<script type="text/babel">
import Emitter from '@utils/mixins/emitter';
import { getValueByPath, escapeRegexpString } from '@utils/utils/util';
import YxtCheckbox from '@backstage/checkbox';

export default {
  mixins: [Emitter],

  name: 'YxtOption',

  componentName: 'YxtOption',

  inject: ['select'],

  components: {
    YxtCheckbox
  },

  props: {
    value: {
      required: true
    },
    label: [String, Number],
    labelProp: {
      type: String,
      default: ''
    },
    created: Boolean,
    disabled: {
      type: Boolean,
      default: false
    },
    labelTooltip: String,
    tooltipEffect: {
      type: String,
      default: 'dark'
    },
    labelTooltipWidth: {
      type: Number,
      default: 100
    }
  },

  data() {
    return {
      index: -1,
      groupDisabled: false,
      visible: true,
      hitState: false
    };
  },

  computed: {
    isObject() {
      return (
        Object.prototype.toString.call(this.value).toLowerCase() ===
        '[object object]'
      );
    },

    isHover() {
      return this.select.options.indexOf(this) === this.select.hoverIndex;
    },
    currentLabel() {
      return this.label || (this.isObject ? '' : this.value);
    },

    currentValue() {
      return this.value || this.label || '';
    },

    itemSelected() {
      let result = false;
      if (!this.select.multiple) {
        result = this.isEqual(this.value, this.select.value);
      } else {
        result = this.contains(this.select.value, this.value);
      }
      return result;
    },

    itemDisabled() {
      return this.disabled || this.groupDisabled || this.limitReached;
    },

    itemSelectedClass() {
      return `selected-${this.select.multipleStyle}`;
    },

    limitReached() {
      if (this.select.multiple) {
        return (
          !this.itemSelected &&
          (this.select.value || []).length >= this.select.multipleLimit &&
          this.select.multipleLimit > 0
        );
      } else {
        return false;
      }
    }
  },

  watch: {
    currentLabel() {
      if (!this.created && !this.select.remote) {
        this.dispatch('YxtSelect', 'setSelected');
      }
    },
    value(val, oldVal) {
      const { remote, valueKey } = this.select;
      if (!this.created && !remote) {
        if (
          valueKey &&
          typeof val === 'object' &&
          typeof oldVal === 'object' &&
          val[valueKey] === oldVal[valueKey]
        ) {
          return;
        }
        this.dispatch('YxtSelect', 'setSelected');
      }
    }
  },

  methods: {
    isEqual(a, b) {
      if (!this.isObject) {
        return a === b;
      } else {
        const valueKey = this.select.valueKey;
        return getValueByPath(a, valueKey) === getValueByPath(b, valueKey);
      }
    },

    contains(arr = [], target) {
      if (!this.isObject) {
        return arr && arr.indexOf(target) > -1;
      } else {
        const valueKey = this.select.valueKey;
        return (
          arr &&
          arr.some(item => {
            return (
              getValueByPath(item, valueKey) ===
              getValueByPath(target, valueKey)
            );
          })
        );
      }
    },

    handleGroupDisabled(val) {
      this.groupDisabled = val;
    },

    hoverItem() {
      if (!this.disabled && !this.groupDisabled && this.select.mouseEnterEnable) {
        this.select.hoverIndex = this.select.options.indexOf(this);
      }
    },

    mouseoutItem() {
      // this.select.hoverIndex = -1;
    },

    enableMouseEnter() {
      if (!this.select.mouseEnterEnable) {
        this.select.mouseEnterEnable = true;
        this.hoverItem();
      }
    },

    selectOptionClick() {
      if (this.disabled !== true && this.groupDisabled !== true) {
        this.dispatch('YxtSelect', 'handleOptionClick', [this, true]);
      }
    },

    queryChange(query) {
      this.visible =
        new RegExp(escapeRegexpString(query), 'i').test(this.currentLabel) ||
        this.created;
      if (!this.visible) {
        this.select.filteredOptionsCount--;
      }
    }
  },

  created() {
    this.select.options.push(this);
    this.select.cachedOptions.push(this);
    this.select.optionsCount++;
    this.select.filteredOptionsCount++;

    this.$on('queryChange', this.queryChange);
    this.$on('handleGroupDisabled', this.handleGroupDisabled);
  },

  beforeDestroy() {
    let index = this.select.cachedOptions.indexOf(this);
    if (index > -1) {
      this.select.cachedOptions.splice(index, 1);
    }
    this.select.onOptionDestroy(this.select.options.indexOf(this));
  }
};
</script>
